import { createContext, useContext, useMemo } from "react";

import { useIntl } from "react-intl";

import { CreateUIAnalyticsEvent } from "@atlaskit/analytics-next";

import { AvailableProductsResponse } from "@/libraries/bxp-web-stargate-client/src";

import { StatsigExperimentKey } from "../../../../feature-gates/types";

import { ExperimentEnrollment, useExperiment } from "./use-experiment";

import {
    UserSegmentationStatusContext,
    useSearchParameters,
    useUserSegmentationStatus
} from ".";

export type ConfirmationPageContext = {
    availableProducts?: AvailableProductsResponse;
    userSegmentationContext: UserSegmentationStatusContext;
    experiments: Map<string, ExperimentEnrollment>;
};

const ConfirmationPageContext = createContext<ConfirmationPageContext>({
    availableProducts: undefined,
    userSegmentationContext: {
        closeUserSegmentationUI: () =>
            console.warn("closeUserSegmentationUI is not defined")
    } as UserSegmentationStatusContext,
    experiments: new Map<string, ExperimentEnrollment>()
});

export const useConfirmationPage = (): ConfirmationPageContext => {
    const context = useContext(ConfirmationPageContext);

    if (context === undefined) {
        throw new Error(
            "`useConfirmationPage` must be used within a `ConfirmationPageContext.Provider`"
        );
    }
    return context;
};

export const ConfirmationPageProvider = ({
    createAnalyticsEvent,
    availableProducts,
    children
}: ConfirmationPageProviderProps) => {
    const { products, templateId, n2n } = useSearchParameters();
    const userSegmentationContext = useUserSegmentationStatus(
        products,
        availableProducts ?? null
    );
    const { locale } = useIntl();

    // conditions that can be used across experiments.
    const hasTemplateRecommendation = Boolean(templateId);
    const isProvisioningOnlyJSW =
        products.length === 1 && products[0] === "jira-software.ondemand";

    const transparentTemplateForAllTeamsExperiment = useExperiment(
        StatsigExperimentKey.JFIND_TRANSPARENT_TEMPLATE_FOR_ALL_TEAMS,
        [
            hasTemplateRecommendation === false,
            isProvisioningOnlyJSW === true,
            n2n === true,
            locale === "en"
        ],
        createAnalyticsEvent
    );

    const experiments = useMemo(
        () =>
            new Map([
                [
                    StatsigExperimentKey.JFIND_TRANSPARENT_TEMPLATE_FOR_ALL_TEAMS,
                    transparentTemplateForAllTeamsExperiment
                ]
            ]),
        [transparentTemplateForAllTeamsExperiment]
    );

    return (
        <ConfirmationPageContext.Provider
            value={{ userSegmentationContext, availableProducts, experiments }}
        >
            {children}
        </ConfirmationPageContext.Provider>
    );
};

export type ConfirmationPageProviderProps = {
    createAnalyticsEvent: CreateUIAnalyticsEvent;
    availableProducts?: AvailableProductsResponse;
} & React.PropsWithChildren;
